// based on https://codesandbox.io/p/sandbox/xenodochial-grass-js3bo9

import React, {
  useRef,
  useMemo,
  useState,
  useContext,
  createContext
} from "react"
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useRole,
  useClick,
  useHover,
  useFocus,
  useDismiss,
  useInteractions,
  useMergeRefs,
  FloatingArrow,
  FloatingPortal
} from "@floating-ui/react"

export function useTooltip({
  initial_open = false,
  placement = "top",
  enabled = true,
  open: controlled_open,
  onOpenChange: set_controlled_open
} = {}) {
  const [uncontrolled_open, set_uncontrolled_open] = useState(initial_open)
  const arrowRef = useRef(null)

  const open = controlled_open ?? uncontrolled_open
  const setOpen = set_controlled_open ?? set_uncontrolled_open

  const data = useFloating({
    placement,
    enabled,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(7),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "start",
        padding: 5
      }),
      shift({padding: 5}),
      arrow({element: arrowRef})
    ]
  })

  const click = useClick(data.context, {ignoreMouse: true})
  const dismiss = useDismiss(data.context)
  const hover = useHover(data.context, {
    move: false,
    enabled: controlled_open == null && enabled
  })
  const focus = useFocus(data.context, {
    enabled: controlled_open == null && enabled
  })
  const role = useRole(data.context, {role: "tooltip"})

  const interactions = useInteractions([hover, focus, role, click, dismiss])

  return useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      ...interactions,
      ...data
    }),
    [open, setOpen, interactions, data]
  )
}

const TooltipContext = createContext(null)

export function useTooltipContext() {
  const context = useContext(TooltipContext)

  if (context == null) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />")
  }

  return context
}

export function Tooltip({children, ...options}) {
  const tooltip = useTooltip(options)
  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  )
}

export const TooltipTrigger = React.forwardRef((
  {children, asChild = false, ...props},
  propRef
) => {
  const context = useTooltipContext()
  const childrenRef = children?.ref
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef])

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        "data-state": context.open ? "open" : "closed"
      })
    )
  }

  return (
    <div
      ref={ref}
      data-state={context.open ? "open" : "closed"}
      {...context.getReferenceProps(props)}
    >
      {children}
    </div>
  )
})

export const TooltipContent = React.forwardRef((
  {style, ...props},
  propRef
) => {
  const context = useTooltipContext()
  const ref = useMergeRefs([context.refs.setFloating, propRef])
  props.className = props.className || "tooltip"

  if (!context.open) return null

  return (
    <FloatingPortal>
      <div
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...style
        }}
        {...context.getFloatingProps(props)}
      >
        {props.children}
        <FloatingArrow
          fill="#3A5062"
          ref={context.arrowRef}
          context={context.context}
        />
      </div>
    </FloatingPortal>
  )
})
